// /opt/dashboard/frontend/src/pages/TopPoiUsers.js
// Top POI-használók panel: Ma / 7 nap / 30 nap rangsor + helyszínek térképen.
import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { TEMPLATES, LANGS, LANG_CODES, FALLBACK_LANG, langLabel } from './emailTemplates';
import { TILE_URL, TILE_ATTRIBUTION } from '../components/basemaps';

const C = {
  bg: '#1a1d27', panel: '#22252f', border: '#2a2e3a', text: '#e0e6ed',
  muted: '#8892a0', accent: '#00d4ff', warning: '#fbbf24', success: '#22c55e',
};

const WINDOWS = [['today', 'Ma'], ['7d', '7 nap'], ['30d', '30 nap']];

// ── Email-kiküldés: alapértelmezett feladó ─────────────────────────────────
// A tárgy és a szöveg nyelvenként az emailTemplates.js-ből jön; a címzett
// users/{uid}.appLanguage mezője dönti el, melyiket kapja.
const DEFAULT_FROM = 'support@aitourmate.eu';

// 'hu-HU' → 'hu'; ismeretlen/hiányzó nyelv → null (ilyenkor angol megy ki)
const normLang = (v) => {
  const raw = (v || '').toString().toLowerCase().split(/[-_]/)[0].trim();
  return LANG_CODES.includes(raw) ? raw : null;
};

const fmtDateTime = (ms) => {
  if (!ms) return '—';
  try { return new Date(ms).toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' }); }
  catch (e) { return '—'; }
};
// email_history.sent_at: SQLite "YYYY-MM-DD HH:MM:SS" UTC-ben → helyi rövid dátum
const fmtSentAt = (s) => {
  if (!s) return '—';
  try {
    const d = new Date(s.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString('hu-HU', { month: '2-digit', day: '2-digit' });
  } catch (e) { return s; }
};
const shortId = (id) => (id && id.length > 12 ? id.slice(0, 8) + '…' + id.slice(-3) : id);
const platLabel = (p) => (p === 'android' ? '🤖 Android' : p === 'ios' ? '🍎 iOS' : p ? '📱 ' + p : '');
const displayName = (u) => u.displayName || u.email || shortId(u.userId);

function typeLabel(t) {
  const m = {
    place_of_worship: '⛪ Templom', attraction: '🎡 Látványosság', museum: '🏛️ Múzeum',
    restaurant: '🍽️ Étterem', park: '🌳 Park', monument: '🗿 Emlékmű',
    viewpoint: '👁️ Kilátó', castle: '🏰 Kastély', unknown: '📍 Egyéb',
  };
  return m[t] || ('📍 ' + t);
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    try {
      const b = L.latLngBounds(points.map(p => [p.lat, p.lon]));
      map.fitBounds(b, { padding: [30, 30], maxZoom: 12 });
    } catch (e) { /* noop */ }
  }, [points, map]);
  return null;
}

// ── Levélszerkesztő ablak ────────────────────────────────────────────────────
// Nyelvérzékelés: minden címzett a saját app-nyelvén kapja a levelet
// (users/{uid}.appLanguage). Ismeretlen vagy hiányzó nyelvnél az ANGOL megy ki,
// ezért az angol sablon kötelező. A nyelvenkénti mód kikapcsolható — olyankor
// mindenki ugyanazt a szöveget kapja.
function ComposeModal({ recipients, onClose, onSent }) {
  const [from, setFrom] = useState(DEFAULT_FROM);
  const [perLanguage, setPerLanguage] = useState(true);
  const [personalize, setPersonalize] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState(null);

  const [list, setList] = useState(() => recipients.map(u => ({
    userId: u.userId, email: u.email, name: displayName(u), lang: normLang(u.appLanguage),
  })));

  // Nyelvenkénti sablonok — szerkeszthetők, kiindulásnak a beépített fordítások
  const [tpls, setTpls] = useState(() => {
    const o = {};
    for (const c of LANG_CODES) o[c] = { ...TEMPLATES[c] };
    return o;
  });

  // Mely nyelvek fordulnak elő a címzettek között (ismeretlen → angol)
  const langCounts = {};
  let unknownLang = 0;
  for (const r of list) {
    const l = r.lang || FALLBACK_LANG;
    if (!r.lang) unknownLang++;
    langCounts[l] = (langCounts[l] || 0) + 1;
  }
  const presentLangs = LANG_CODES.filter(c => langCounts[c]);
  // Az angol mindig legyen szerkeszthető: az ismeretlen nyelvűek tartaléka
  const tabLangs = presentLangs.includes(FALLBACK_LANG) ? presentLangs : [FALLBACK_LANG, ...presentLangs];

  const [activeTab, setActiveTab] = useState(() => {
    const first = LANG_CODES.filter(c => langCounts[c]);
    return first[0] || FALLBACK_LANG;
  });

  const cur = tpls[activeTab] || { subject: '', bodyText: '' };
  const setCur = (patch) => setTpls(prev => ({ ...prev, [activeTab]: { ...prev[activeTab], ...patch } }));
  const resetCur = () => setTpls(prev => ({ ...prev, [activeTab]: { ...TEMPLATES[activeTab] } }));

  const removeOne = (userId) => setList(prev => prev.filter(r => r.userId !== userId));

  // A küldéshez összeállított payload (a teszt és az éles küldés is ezt használja)
  const buildPayload = (recips) => (perLanguage
    ? {
      confirm: true, from: from.trim(), personalize, mode: 'perLanguage',
      templates: Object.fromEntries(tabLangs.map(c => [c, tpls[c]])),
      recipients: recips,
    }
    : {
      confirm: true, from: from.trim(), personalize,
      subject: cur.subject.trim(), bodyText: cur.bodyText,
      recipients: recips,
    });

  // Teszt: a levél elküldése EGY címre (a valódi címzettek nem kapják meg).
  // A teszt az ÉPPEN NYITOTT nyelvi fület küldi, hogy azt lehessen ellenőrizni.
  const sendTest = () => {
    const to = testTo.trim();
    setError(null); setTestMsg(null);
    if (!to) { setError('Adj meg egy email címet a teszthez'); return; }
    if (!cur.subject.trim() || !cur.bodyText.trim()) { setError('A tárgy és a levél törzse kötelező'); return; }
    setSending(true);
    fetch('/api/email-send/send', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        confirm: true, from: from.trim(), personalize,
        subject: cur.subject.trim(), bodyText: cur.bodyText,
        recipients: [{ email: to, name: 'Teszt Címzett', lang: activeTab }],
      }),
    })
      .then(async r => { const j = await r.json(); if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status)); return j; })
      .then(j => {
        if (j.failed) setError('Teszt sikertelen: ' + ((j.results && j.results[0] && j.results[0].error) || 'ismeretlen hiba'));
        else setTestMsg(`✅ Teszt levél elküldve (${langLabel(activeTab)}): ${to}`);
      })
      .catch(e => setError(e.message))
      .finally(() => setSending(false));
  };

  const send = () => {
    setError(null); setResult(null);
    if (list.length === 0) { setError('Nincs címzett'); return; }
    if (perLanguage) {
      const missing = tabLangs.filter(c => !tpls[c] || !tpls[c].subject.trim() || !tpls[c].bodyText.trim());
      if (missing.length) { setError('Hiányos sablon: ' + missing.map(langLabel).join(', ')); return; }
    } else if (!cur.subject.trim() || !cur.bodyText.trim()) {
      setError('A tárgy és a levél törzse kötelező'); return;
    }
    const what = perLanguage
      ? `${list.length} címzettnek, ${tabLangs.length} nyelven`
      : `${list.length} címzettnek (mind ${langLabel(activeTab)} nyelven)`;
    if (!window.confirm(`Biztosan elküldöd a levelet ${what}?`)) return;

    setSending(true);
    fetch('/api/email-send/send', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload(list.map(r => ({ email: r.email, name: r.name, lang: r.lang })))),
    })
      .then(async r => { const j = await r.json(); if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status)); return j; })
      .then(j => { setResult(j); if (onSent) onSent(); })
      .catch(e => setError(e.message))
      .finally(() => setSending(false));
  };

  const inp = {
    width: '100%', padding: '9px 11px', borderRadius: 6, fontSize: 13,
    background: C.bg, border: '1px solid ' + C.border, color: C.text, boxSizing: 'border-box',
  };
  const lbl = { fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 5, display: 'block' };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: C.panel, border: '1px solid ' + C.border, borderRadius: 12,
        width: '100%', maxWidth: 700, padding: 20, marginTop: 24, marginBottom: 24,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>✉ Email írása</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {result ? (
          <div>
            <div style={{
              background: result.failed ? 'rgba(251,191,36,0.1)' : 'rgba(34,197,94,0.1)',
              border: '1px solid ' + (result.failed ? C.warning : C.success),
              borderRadius: 8, padding: 14, marginBottom: 14, color: C.text, fontSize: 14,
            }}>
              <b>{result.failed ? '⚠ Részben sikeres' : '✅ Elküldve'}</b> — {result.success} sikeres
              {result.failed > 0 ? `, ${result.failed} hibás` : ''} ({result.total} címzettből).
            </div>

            {result.byLanguage && result.byLanguage.length > 0 && (
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
                <b style={{ color: C.text }}>Nyelvenkénti bontás:</b>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                  {result.byLanguage.map(b => (
                    <span key={b.lang} style={{
                      background: C.bg, border: '1px solid ' + C.border, borderRadius: 20,
                      padding: '3px 10px', color: C.text,
                    }} title={b.fallback ? `${b.fallback} címzett tartalékként kapta ezt a nyelvet` : ''}>
                      {langLabel(b.lang)}: {b.count}
                      {b.fallback > 0 && <span style={{ color: C.warning }}> ({b.fallback} tartalék)</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {result.results && result.results.some(r => !r.ok) && (
              <div style={{ maxHeight: 180, overflowY: 'auto', fontSize: 12, marginBottom: 14 }}>
                {result.results.filter(r => !r.ok).map((r, i) => (
                  <div key={i} style={{ color: '#ef4444', padding: '3px 0' }}>✗ {r.email}: {r.error}</div>
                ))}
              </div>
            )}
            <button onClick={onClose} style={{
              padding: '9px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700,
              border: 'none', background: C.accent, color: '#0a0d14',
            }}>Bezárás</button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Feladó</label>
              <input value={from} onChange={e => setFrom(e.target.value)} style={inp} placeholder="support@aitourmate.eu" />
            </div>

            {/* Nyelvérzékelés kapcsoló */}
            <div style={{
              background: C.bg, border: '1px solid ' + (perLanguage ? C.accent : C.border),
              borderRadius: 8, padding: '10px 12px', marginBottom: 12,
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text, cursor: 'pointer', fontWeight: 600 }}>
                <input type="checkbox" checked={perLanguage} onChange={e => setPerLanguage(e.target.checked)} style={{ accentColor: C.accent }} />
                🌍 Mindenki a saját nyelvén kapja
              </label>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
                {perLanguage ? (
                  <>
                    A címzett app-nyelve (<code style={{ color: C.accent }}>appLanguage</code>) dönti el, melyik szöveg megy ki.
                    {unknownLang > 0 && (
                      <> <b style={{ color: C.warning }}>{unknownLang} címzettnél nincs ismert nyelv</b> — ők az angol változatot kapják.</>
                    )}
                  </>
                ) : (
                  <>Kikapcsolva: <b style={{ color: C.text }}>mindenki</b> a most nyitott nyelv ({langLabel(activeTab)}) szövegét kapja.</>
                )}
              </div>
            </div>

            {/* Nyelvi fülek */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {tabLangs.map(c => {
                const active = activeTab === c;
                const n = langCounts[c] || 0;
                return (
                  <button key={c} onClick={() => setActiveTab(c)} style={{
                    padding: '5px 11px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                    fontWeight: active ? 700 : 500,
                    border: '1px solid ' + (active ? C.accent : C.border),
                    background: active ? 'rgba(0,212,255,0.12)' : 'transparent',
                    color: active ? C.accent : C.muted,
                  }}>
                    {langLabel(c)}{n > 0 ? ` · ${n}` : ''}
                    {c === FALLBACK_LANG && unknownLang > 0 ? ` (+${unknownLang})` : ''}
                  </button>
                );
              })}
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={lbl}>Tárgy — {langLabel(activeTab)}</label>
                <button onClick={resetCur} style={{
                  background: 'none', border: 'none', color: C.muted, fontSize: 11,
                  cursor: 'pointer', textDecoration: 'underline', padding: 0, marginBottom: 5,
                }}>eredeti szöveg visszaállítása</button>
              </div>
              <input value={cur.subject} onChange={e => setCur({ subject: e.target.value })} style={inp} />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Levél szövege — {langLabel(activeTab)}</label>
              <textarea value={cur.bodyText} onChange={e => setCur({ bodyText: e.target.value })} rows={14}
                style={{ ...inp, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.muted, marginBottom: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={personalize} onChange={e => setPersonalize(e.target.checked)} style={{ accentColor: C.accent }} />
              Személyre szabás — a <code style={{ color: C.accent }}>{'{{name}}'}</code> helyére a címzett keresztneve kerül
              (név hiányában a levél nyelvén álló megszólítás)
            </label>

            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Címzettek ({list.length})</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 120, overflowY: 'auto' }}>
                {list.map(r => (
                  <span key={r.userId} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
                    background: C.bg, border: '1px solid ' + C.border, borderRadius: 20, padding: '4px 10px', color: C.text,
                  }} title={r.name || ''}>
                    <span style={{ color: r.lang ? C.accent : C.warning, fontSize: 10, fontWeight: 700 }}>
                      {r.lang ? r.lang.toUpperCase() : 'EN?'}
                    </span>
                    {r.email}
                    <span onClick={() => removeOne(r.userId)} style={{ cursor: 'pointer', color: C.muted, fontWeight: 700 }}>×</span>
                  </span>
                ))}
                {list.length === 0 && <span style={{ color: C.muted, fontSize: 12 }}>Nincs címzett — zárd be és jelölj ki usereket.</span>}
              </div>
            </div>

            {/* Teszt-küldés */}
            <div style={{ borderTop: '1px solid ' + C.border, paddingTop: 12, marginBottom: 14 }}>
              <label style={lbl}>Teszt — a most nyitott nyelv ({langLabel(activeTab)}) egy címre</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={testTo} onChange={e => setTestTo(e.target.value)} style={{ ...inp, flex: 1 }}
                  placeholder="sajat@email.hu" />
                <button onClick={sendTest} disabled={sending} style={{
                  padding: '9px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  border: '1px solid ' + C.border, background: 'transparent', color: C.text, whiteSpace: 'nowrap',
                }}>Teszt</button>
              </div>
              {testMsg && <div style={{ color: C.success, fontSize: 12, marginTop: 6 }}>{testMsg}</div>}
            </div>

            {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>Hiba: {error}</div>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={onClose} disabled={sending} style={{
                padding: '9px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                border: '1px solid ' + C.border, background: 'transparent', color: C.muted,
              }}>Mégse</button>
              <button onClick={send} disabled={sending || list.length === 0} style={{
                padding: '9px 20px', borderRadius: 6, cursor: sending ? 'default' : 'pointer', fontSize: 13, fontWeight: 700,
                border: 'none', background: sending ? C.muted : C.accent, color: '#0a0d14', opacity: list.length === 0 ? 0.5 : 1,
              }}>{sending ? 'Küldés…' : `Küldés (${list.length})`}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function TopPoiUsers() {
  const [win, setWin] = useState('today');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // userId (térkép kibontás)
  const [copiedId, setCopiedId] = useState(null);
  const [picked, setPicked] = useState(() => new Set()); // email-kijelölés (userId-k)
  const [compose, setCompose] = useState(false);         // levélszerkesztő nyitva?
  const [sentMap, setSentMap] = useState({});            // email → { count, lastSentAt, success }

  // Kinek ment már ki outreach levél (email_history alapján)
  const loadSentMap = () => {
    fetch('/api/email-send/sent-map', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : {}))
      .then(m => setSentMap(m || {}))
      .catch(() => { /* nem kritikus — a jelölés hiánya nem hiba */ });
  };

  useEffect(() => { loadSentMap(); }, []);

  const sentInfo = (u) => (u.email ? sentMap[u.email.toLowerCase()] : null);

  const togglePick = (e, u) => {
    if (e) e.stopPropagation();
    if (!u.email) return;
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(u.userId)) next.delete(u.userId); else next.add(u.userId);
      return next;
    });
  };

  const copyEmail = (e, u) => {
    if (e) e.stopPropagation();
    if (!u.email) return;
    try {
      navigator.clipboard.writeText(u.email);
      setCopiedId(u.userId);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (err) { /* noop */ }
  };

  const load = (w) => {
    setLoading(true); setError(null); setSelected(null); setPicked(new Set());
    fetch(`/api/poi-usage/top-users?window=${w}`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(d => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(win); /* eslint-disable-next-line */ }, [win]);

  const sel = data && selected ? data.users.find(u => u.userId === selected) : null;

  // Email-kijelöléshez: csak azok a userek jelölhetők, akiknek van email címük.
  const emailable = (data ? data.users : []).filter(u => u.email);
  const pickedUsers = (data ? data.users : []).filter(u => picked.has(u.userId) && u.email);
  const allPicked = emailable.length > 0 && emailable.every(u => picked.has(u.userId));
  const toggleAll = () => {
    setPicked(prev => {
      if (emailable.every(u => prev.has(u.userId))) return new Set();
      return new Set(emailable.map(u => u.userId));
    });
  };

  // Akiknek még NEM ment ki levél — a leggyakoribb kijelölés outreach-nél.
  const notYetSent = emailable.filter(u => !sentInfo(u));
  const pickNotYetSent = () => setPicked(new Set(notYetSent.map(u => u.userId)));
  const alreadySentPicked = pickedUsers.filter(u => sentInfo(u)).length;

  const cardStyle = { background: C.panel, border: '1px solid ' + C.border, borderRadius: 8, padding: 16 };

  return (
    <div style={{ padding: 20, minHeight: '100vh', background: C.bg }}>
      <div style={{ ...cardStyle }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>🏆 Top POI-használók</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {WINDOWS.map(([w, label]) => {
            const active = win === w;
            return (
              <button key={w} onClick={() => setWin(w)} style={{
                padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                border: '1px solid ' + (active ? C.accent : C.border),
                background: active ? 'rgba(0,212,255,0.12)' : 'transparent',
                color: active ? C.accent : C.muted,
              }}>{label}</button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ color: C.muted, fontSize: 13 }}>Betöltés…</div>
      ) : error ? (
        <div style={{ color: '#ef4444', fontSize: 13 }}>Hiba: {error}</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 20, marginBottom: 12, fontSize: 13, color: C.muted, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>Aktív használók: <b style={{ color: C.text }}>{data.activeUsers}</b></span>
            <span>Összes POI: <b style={{ color: C.accent }}>{data.totalPois}</b></span>
            {emailable.length > 0 && (
              <>
                <span style={{ color: C.muted }}>Email-lel: <b style={{ color: C.text }}>{emailable.length}</b></span>
                <span style={{ color: C.muted }}>
                  Levél kiment: <b style={{ color: C.success }}>{emailable.length - notYetSent.length}</b>
                  {' · '}még nem: <b style={{ color: C.warning }}>{notYetSent.length}</b>
                </span>
                {notYetSent.length > 0 && (
                  <button onClick={pickNotYetSent} style={{
                    padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    border: '1px solid ' + C.accent, background: 'transparent', color: C.accent,
                  }}>Kijelölés: akiknek még nem ment ({notYetSent.length})</button>
                )}
              </>
            )}
          </div>

          {/* Email-akciósáv — csak ha van kijelölt címzett */}
          {picked.size > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              background: 'rgba(0,212,255,0.08)', border: '1px solid ' + C.accent,
              borderRadius: 8, padding: '10px 14px', marginBottom: 12,
            }}>
              <span style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>
                ✉ {pickedUsers.length} címzett kijelölve
              </span>
              {alreadySentPicked > 0 && (
                <span style={{ color: C.warning, fontSize: 12 }}>
                  ⚠ ebből {alreadySentPicked} már kapott levelet
                </span>
              )}
              <button onClick={() => setCompose(true)} style={{
                padding: '7px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                border: 'none', background: C.accent, color: '#0a0d14',
              }}>✉ Email írása</button>
              <button onClick={() => setPicked(new Set())} style={{
                padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                border: '1px solid ' + C.border, background: 'transparent', color: C.muted,
              }}>Kijelölés törlése</button>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: C.muted, textAlign: 'left', borderBottom: '1px solid ' + C.border }}>
                  <th style={{ padding: '6px 8px', width: 28 }}>
                    <input type="checkbox" checked={allPicked} onChange={toggleAll}
                      title="Összes email-lel rendelkező kijelölése"
                      style={{ cursor: 'pointer', accentColor: C.accent }} />
                  </th>
                  <th style={{ padding: '6px 8px', width: 36 }}>#</th>
                  <th style={{ padding: '6px 8px' }}>Felhasználó</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>POI</th>
                  <th style={{ padding: '6px 8px' }}>Top típusok</th>
                  <th style={{ padding: '6px 8px' }}>Utolsó</th>
                  <th style={{ padding: '6px 8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u, i) => {
                  const active = selected === u.userId;
                  return (
                    <tr key={u.userId}
                      onClick={() => setSelected(active ? null : u.userId)}
                      style={{ borderBottom: '1px solid ' + C.border, cursor: 'pointer', background: picked.has(u.userId) ? 'rgba(0,212,255,0.1)' : active ? 'rgba(0,212,255,0.06)' : 'transparent' }}>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        {u.email ? (
                          <input type="checkbox" checked={picked.has(u.userId)} onChange={(e) => togglePick(e, u)}
                            style={{ cursor: 'pointer', accentColor: C.accent }} />
                        ) : (
                          <span title="Nincs email cím" style={{ color: C.border }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', color: i < 3 ? C.warning : C.muted, fontWeight: 700 }}>{i + 1}</td>
                      <td style={{ padding: '6px 8px' }} title={u.userId}>
                        <div style={{ color: C.text, fontWeight: 600 }}>{displayName(u)}</div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                          {[shortId(u.userId), platLabel(u.platform), u.lastCity].filter(Boolean).join(' · ')}
                        </div>
                        {u.email && (
                          <div style={{ fontSize: 11, marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span onClick={(e) => copyEmail(e, u)} title="Kattints a másoláshoz"
                              style={{ color: C.accent, cursor: 'pointer', userSelect: 'all' }}>
                              ✉ {u.email} {copiedId === u.userId ? '✓' : '📋'}
                            </span>
                            {(() => {
                              const s = sentInfo(u);
                              if (!s) return null;
                              const label = s.success ? '✅ levél kiment' : '⚠ küldés hibás';
                              const color = s.success ? C.success : C.warning;
                              return (
                                <span title={`${s.count} db · utolsó: ${fmtSentAt(s.lastSentAt)}`}
                                  style={{
                                    color, border: '1px solid ' + color, borderRadius: 10,
                                    padding: '1px 7px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap',
                                  }}>
                                  {label} · {fmtSentAt(s.lastSentAt)}{s.count > 1 ? ` (${s.count}×)` : ''}
                                </span>
                              );
                            })()}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: C.accent }}>{u.poiCount}</td>
                      <td style={{ padding: '6px 8px', color: C.muted }}>
                        {u.topTypes.map(t => `${typeLabel(t.type)} ${t.count}`).join(' · ')}
                      </td>
                      <td style={{ padding: '6px 8px', color: C.muted }}>{fmtDateTime(u.lastTs)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: C.accent }}>{active ? '▲' : '▼ térkép'}</td>
                    </tr>
                  );
                })}
                {data.users.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 12, color: C.muted }}>Nincs POI-használat ebben az időszakban</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Kiválasztott user: térkép + POI-lista */}
          {sel && (
            <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(240px, 340px)', gap: 12 }}>
              <div style={{ height: 380, borderRadius: 8, overflow: 'hidden', border: '1px solid ' + C.border }}>
                {sel.pois.length > 0 ? (
                  <MapContainer center={[sel.pois[0].lat, sel.pois[0].lon]} zoom={5} style={{ height: '100%', width: '100%', background: '#0a0d14' }} scrollWheelZoom={false}>
                    <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
                    <FitBounds points={sel.pois} />
                    {sel.pois.map((p, idx) => (
                      <CircleMarker key={idx} center={[p.lat, p.lon]} radius={6} pathOptions={{ color: '#00d4ff', fillColor: '#00d4ff', fillOpacity: 0.6, weight: 1 }}>
                        <Popup><b>{p.name || '—'}</b><br />{typeLabel(p.type)}<br />{fmtDateTime(p.ts)}</Popup>
                      </CircleMarker>
                    ))}
                  </MapContainer>
                ) : (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: 13 }}>
                    Nincs koordináta ehhez a felhasználóhoz
                  </div>
                )}
              </div>

              <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid ' + C.border, borderRadius: 8 }}>
                <div style={{ padding: '8px 10px', borderBottom: '1px solid ' + C.border, position: 'sticky', top: 0, background: C.panel }}>
                  <div style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>{displayName(sel)}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    {[sel.email, platLabel(sel.platform), sel.lastCity].filter(Boolean).join(' · ') || '—'}
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 2, fontFamily: 'monospace' }}>{sel.userId}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sel.pois.length} POI a listában</div>
                </div>
                {sel.pois.map((p, idx) => (
                  <div key={idx} style={{ padding: '8px 10px', borderBottom: '1px solid ' + C.border, fontSize: 12 }}>
                    <div style={{ color: C.text, fontWeight: 600 }}>{p.name || '—'}</div>
                    <div style={{ color: C.muted, marginTop: 2 }}>
                      {typeLabel(p.type)} · {fmtDateTime(p.ts)}
                      {p.lat != null && (
                        <> · <a href={`https://www.google.com/maps?q=${p.lat},${p.lon}`} target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>📍 térkép</a></>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      </div>

      {compose && (
        <ComposeModal recipients={pickedUsers} onClose={() => setCompose(false)} onSent={loadSentMap} />
      )}
    </div>
  );
}
